import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  LayoutGrid,
  List,
  Briefcase,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useProjects, useClients } from '@/hooks/use-projects';
import { usePermissions } from '@/hooks/use-permissions';
import { useOrganizationSettings, useOrgCurrency } from '@/hooks/use-settings';
import { useIndustry } from '@/hooks/use-industry';
import { formatMoney, OPTIONAL_FRACTION_DIGITS } from '@/lib/currency';
import { formatDateOnly } from '@/lib/date-utils';
import type { ProjectStatus } from '@/lib/types';

const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'bidding', label: 'Bidding' },
  { value: 'awarded', label: 'Awarded' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const KANBAN_COLUMNS: { status: ProjectStatus; label: string; color: string }[] = [
  { status: 'draft', label: 'Draft', color: 'bg-gray-100' },
  { status: 'bidding', label: 'Bidding', color: 'bg-blue-50' },
  { status: 'awarded', label: 'Awarded', color: 'bg-purple-50' },
  { status: 'active', label: 'Active', color: 'bg-green-50' },
  { status: 'completed', label: 'Completed', color: 'bg-emerald-50' },
];

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  bidding: 'bg-blue-100 text-blue-800',
  awarded: 'bg-purple-100 text-purple-800',
  active: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

const priorityColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  critical: 'bg-red-100 text-red-600',
};

export function ProjectsListPage() {
  const currency = useOrgCurrency();
  const [view, setView] = useState<'table' | 'kanban'>('table');
  const { data: orgSettings } = useOrganizationSettings();
  const projectsEnabled = orgSettings?.modules.projects ?? false;
  const effectiveView = projectsEnabled ? view : 'table';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('');
  const [clientFilter, setClientFilter] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const { labels } = useIndustry();

  // Clients are Admin/Manager-only on the server; don't ask for them (and
  // don't offer the filter) for anyone else.
  const { canViewClients, canManageJobs } = usePermissions();
  // Same rule as the server's job write permission (see canManageJobs), so
  // nobody is offered a button that the API will reject.
  const canCreateJob = canManageJobs;
  const { data: clients } = useClients({ page_size: 100, is_active: true }, canViewClients);
  const { data, isLoading } = useProjects({
    search,
    status: statusFilter || undefined,
    client: clientFilter,
    page,
    page_size: effectiveView === 'kanban' ? 100 : 20,
  });

  // For kanban, group projects by status
  const kanbanData = KANBAN_COLUMNS.map((col) => ({
    ...col,
    projects: data?.results.filter((p) => p.status === col.status) ?? [],
  }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-600 mt-1">
            {data?.count ?? 0} total projects
          </p>
        </div>
        <div className="flex gap-2">
          {projectsEnabled && (
            <div className="flex border rounded-lg overflow-hidden">
              <Button
                variant={view === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('table')}
                className="rounded-none"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={view === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('kanban')}
                className="rounded-none"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          )}
          {canCreateJob && (
            <Button asChild>
              <Link to="/projects/new">
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search projects..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(val) => {
            setStatusFilter(val as ProjectStatus | '');
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canViewClients && (
          <Select
            value={clientFilter?.toString() ?? 'all'}
            onValueChange={(val) => {
              setClientFilter(val === 'all' ? undefined : parseInt(val));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder={`All ${labels.client}s`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All {labels.client}s</SelectItem>
              {clients?.results.map((c) => (
                <SelectItem key={c.id} value={c.id.toString()}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : effectiveView === 'table' ? (
        /* Table View */
        <>
          {data && data.results.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50 text-left text-gray-500">
                        <th className="p-3 font-medium">Project</th>
                        <th className="p-3 font-medium">{labels.client}</th>
                        <th className="p-3 font-medium">Status</th>
                        <th className="p-3 font-medium">Priority</th>
                        {projectsEnabled && (
                          <>
                            <th className="p-3 font-medium">Department</th>
                            <th className="p-3 font-medium">Budget</th>
                          </>
                        )}
                        <th className="p-3 font-medium">Start Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.results.map((project) => (
                        <tr key={project.id} className="hover:bg-gray-50">
                          <td className="p-3">
                            <Link
                              to={`/projects/${project.id}`}
                              className="font-medium text-blue-600 hover:underline"
                            >
                              {project.name}
                            </Link>
                            <p className="text-gray-500 text-xs">{project.project_number}</p>
                          </td>
                          <td className="p-3 text-gray-600">{project.client_name ?? '—'}</td>
                          <td className="p-3">
                            <Badge className={statusColors[project.status || ''] ?? ''}>
                              {project.status ? project.status.replace('_', ' ') : '—'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Badge className={priorityColors[project.priority || ''] ?? ''}>
                              {project.priority || '—'}
                            </Badge>
                          </td>
                          {projectsEnabled && (
                            <>
                              <td className="p-3 text-gray-600">{project.department_name || '—'}</td>
                              <td className="p-3 text-gray-600">
                                {project.budget
                                  ? formatMoney(project.budget, currency, OPTIONAL_FRACTION_DIGITS)
                                  : '—'}
                              </td>
                            </>
                          )}
                          <td className="p-3 text-gray-600">
                            {formatDateOnly(project.start_date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No projects found</h3>
                <p className="text-gray-500 mt-1">
                  {search || statusFilter
                    ? 'Try adjusting your filters'
                    : 'Get started by creating your first project'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {data && data.count > 20 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, data.count)} of {data.count}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!data.previous}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data.next}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : effectiveView === 'kanban' ? (
        /* Kanban View */
        <div className="flex gap-4 overflow-x-auto pb-4">
          {kanbanData.map((col) => (
            <div key={col.status} className="flex-shrink-0 w-72">
              <div className={`p-3 rounded-t-lg ${col.color}`}>
                <h3 className="font-semibold text-gray-900">
                  {col.label}{' '}
                  <span className="text-gray-500 font-normal">({col.projects.length})</span>
                </h3>
              </div>
              <div className="space-y-2 p-2 bg-gray-50 rounded-b-lg min-h-[200px]">
                {col.projects.map((project) => (
                  <Link key={project.id} to={`/projects/${project.id}`}>
                    <Card className="hover:shadow-md transition-shadow cursor-pointer">
                      <CardContent className="p-3">
                        <p className="font-medium text-sm">{project.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{project.project_number}</p>
                        {project.client_name && (
                          <p className="text-xs text-gray-500">{project.client_name}</p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <Badge className={`text-xs ${priorityColors[project.priority || ''] ?? ''}`}>
                            {project.priority || '—'}
                          </Badge>
                          {project.budget && (
                            <span className="text-xs text-gray-500">
                              {formatMoney(project.budget, currency, OPTIONAL_FRACTION_DIGITS)}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
                {col.projects.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-8">No projects</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
